import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { JwtService } from '../services/jwt.service';
import { combineLatest, distinctUntilChanged, filter, map, Observable, Subscription } from 'rxjs';
import { TitleService } from '../services/hasTitle.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserPlatformService } from '../services/user.service';
import { AgentService } from '../services/agent.service';
import { logDebug, logError } from '../core/services/logger.service';

interface MenuItem {
  icon: SafeHtml;
  label: string;
  route: string;
  badge?: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  template: `
    <div class="h-full flex flex-col overflow-y-auto bg-white">
      <!-- Logo -->
      <div *ngIf="hastTitle" class="px-6 py-6 border-b border-gray-200">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 rounded-lg flex items-center justify-center gradient-primary">
          <svg fill="#FFFFFF" height="200px" width="200px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512.002 512.002" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <g> <g> <path d="M128.257,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533s8.533-3.823,8.533-8.533 S132.967,392.533,128.257,392.533z"></path> <path d="M179.457,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533s8.533-3.823,8.533-8.533 S184.167,392.533,179.457,392.533z"></path> <path d="M247.468,273.067h-68.267c-4.719,0-8.533,3.823-8.533,8.533s3.814,8.533,8.533,8.533h68.267 c4.719,0,8.533-3.823,8.533-8.533S252.187,273.067,247.468,273.067z"></path> <path d="M213.334,324.267h-34.133c-4.719,0-8.533,3.823-8.533,8.533c0,4.71,3.814,8.533,8.533,8.533h34.133 c4.719,0,8.533-3.823,8.533-8.533C221.868,328.09,218.053,324.267,213.334,324.267z"></path> <path d="M358.401,298.667c-9.412,0-17.067-7.654-17.067-17.067c0-4.71-3.814-8.533-8.533-8.533s-8.533,3.823-8.533,8.533 c0,15.855,10.914,29.107,25.6,32.922v1.212c0,4.71,3.814,8.533,8.533,8.533c4.719,0,8.533-3.823,8.533-8.533v-1.212 c14.686-3.814,25.6-17.067,25.6-32.922c0-18.825-15.309-34.133-34.133-34.133c-9.412,0-17.067-7.654-17.067-17.067 c0-9.412,7.654-17.067,17.067-17.067c9.412,0,17.067,7.654,17.067,17.067c0,4.71,3.814,8.533,8.533,8.533 s8.533-3.823,8.533-8.533c0-15.855-10.914-29.107-25.6-32.922v-1.212c0-4.71-3.814-8.533-8.533-8.533 c-4.719,0-8.533,3.823-8.533,8.533v1.212c-14.686,3.814-25.6,17.067-25.6,32.922c0,18.825,15.309,34.133,34.133,34.133 c9.412,0,17.067,7.654,17.067,17.067C375.468,291.012,367.813,298.667,358.401,298.667z"></path> <path d="M333.057,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533c4.71,0,8.533-3.823,8.533-8.533 S337.768,392.533,333.057,392.533z"></path> <path d="M435.457,409.6c4.71,0,8.533-3.823,8.533-8.533s-3.823-8.533-8.533-8.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533 S430.747,409.6,435.457,409.6z"></path> <path d="M384.257,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533c4.71,0,8.533-3.823,8.533-8.533 S388.968,392.533,384.257,392.533z"></path> <path d="M349.868,102.4h34.133c4.719,0,8.533-3.823,8.533-8.533c0-4.71-3.814-8.533-8.533-8.533h-34.133 c-4.719,0-8.533,3.823-8.533,8.533C341.334,98.577,345.149,102.4,349.868,102.4z"></path> <path d="M230.657,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533s8.533-3.823,8.533-8.533 S235.367,392.533,230.657,392.533z"></path> <path d="M128.001,290.133h17.067c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533S123.282,290.133,128.001,290.133z"></path> <path d="M128.001,341.333h17.067c4.719,0,8.533-3.823,8.533-8.533c0-4.71-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533C119.468,337.51,123.282,341.333,128.001,341.333z"></path> <path d="M469.334,0H42.668c-4.719,0-8.533,3.823-8.533,8.533v494.933c0,3.447,2.074,6.562,5.265,7.885 c1.058,0.435,2.167,0.648,3.268,0.648c2.219,0,4.403-0.87,6.033-2.5l45.167-45.167l45.167,45.167 c3.337,3.337,8.73,3.337,12.066,0l28.1-28.1l28.1,28.1c3.337,3.337,8.73,3.337,12.066,0l28.1-28.1l11.034,11.034 c3.337,3.336,8.73,3.336,12.066,0l28.1-28.1l28.1,28.1c3.337,3.336,8.73,3.336,12.066,0l19.567-19.567l19.567,19.567 c3.337,3.336,8.73,3.336,12.066,0l45.167-45.167l28.1,28.1c0.171,0.179,0.35,0.341,0.538,0.495c0,0.009,0.008,0.009,0.008,0.009 v0.009c1.399,1.169,3.174,1.894,5.112,1.98c0.316,0.009,0.631,0.009,0.964-0.009h0.009c1.724-0.128,3.311-0.751,4.599-1.749 c0.461-0.35,0.879-0.751,1.254-1.186c1.289-1.493,2.074-3.43,2.082-5.564v-0.017V8.533C477.868,3.823,474.053,0,469.334,0z M460.801,448.734l-19.567-19.567c-3.337-3.336-8.73-3.336-12.066,0l-45.167,45.167l-19.567-19.567 c-3.336-3.337-8.73-3.337-12.066,0l-19.567,19.567l-28.1-28.1c-3.336-3.337-8.73-3.337-12.066,0l-28.1,28.1L253.501,463.3 c-3.337-3.337-8.73-3.337-12.066,0l-28.1,28.1l-28.1-28.1c-1.664-1.664-3.849-2.5-6.033-2.5c-2.185,0-4.369,0.836-6.033,2.5 l-28.1,28.1l-45.167-45.167c-3.337-3.337-8.73-3.337-12.066,0l-36.634,36.634V17.067h409.6V448.734z"></path> <path d="M77.057,409.6c4.71,0,8.533-3.823,8.533-8.533s-3.823-8.533-8.533-8.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533 S72.347,409.6,77.057,409.6z"></path> <path d="M128.001,238.933h17.067c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533S123.282,238.933,128.001,238.933z"></path> <path d="M281.857,409.6c4.71,0,8.533-3.823,8.533-8.533s-3.823-8.533-8.533-8.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533 S277.147,409.6,281.857,409.6z"></path> <path d="M179.201,187.733h51.2c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-51.2 c-4.719,0-8.533,3.823-8.533,8.533S174.482,187.733,179.201,187.733z"></path> <path d="M128.001,187.733h17.067c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533S123.282,187.733,128.001,187.733z"></path> <path d="M264.534,221.867h-85.333c-4.719,0-8.533,3.823-8.533,8.533s3.814,8.533,8.533,8.533h85.333 c4.719,0,8.533-3.823,8.533-8.533S269.253,221.867,264.534,221.867z"></path> <path d="M128.001,102.4h136.533c4.719,0,8.533-3.823,8.533-8.533c0-4.71-3.814-8.533-8.533-8.533H128.001 c-4.719,0-8.533,3.823-8.533,8.533C119.468,98.577,123.282,102.4,128.001,102.4z"></path> </g> </g> </g> </g></svg>
          </div>
          <div>
            <h2 class="text-xl font-bold text-gray-900">Invoice</h2>
            <p class="text-sm text-gray-500">Pro Platform</p>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 px-4 py-6 space-y-1">
        <div class="mb-6">
          <div *ngIf="showAdminSection$ | async">
            <h3 class="px-4 mb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ 'SIDEBAR.ADMIN' | translate }}</h3>
            <div class="flex flex-col space-y-1">
              <a *ngFor="let item of mainMenuItems$ | async"
                 [routerLink]="item.route"
                 [class.active]="isRouteActive(item.route)"
                 (click)="onLinkClick()"
                 class="sidebar-link">
                <span [innerHTML]="item.icon" class="w-5 h-5 mr-3 flex-shrink-0"></span>
                <span class="flex-1">{{ item.label | translate}}</span>
                <span *ngIf="item.badge" class="ml-2 badge badge-info">{{ item.badge }}</span>
              </a>
            </div>
          </div>
          <div *ngIf="showAgentSection$ | async">
            <h3 class="px-4 my-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ 'SIDEBAR.AGENT' | translate }}</h3>
            <div class="flex flex-col space-y-1">
              <a *ngFor="let item of agentMenuItems"
                 [routerLink]="item.route"
                 [class.active]="isRouteActive(item.route)"
                 (click)="onLinkClick()"
                 class="sidebar-link">
                <span [innerHTML]="item.icon" class="w-5 h-5 mr-3 flex-shrink-0"></span>
                <span class="flex-1">{{ item.label | translate}}</span>
                <span *ngIf="item.badge" class="ml-2 badge badge-info">{{ item.badge }}</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

    </div>
  `
})
export class SidebarComponent implements OnInit, OnDestroy {

  @Output() linkClick = new EventEmitter<void>();
  private tokenSubscription: Subscription | null = null;
  isAgent$ : Observable<boolean>;
  isAdmin$ : Observable<boolean>;
  mainMenuItems$: Observable<MenuItem[]>;
  showAdminSection$: Observable<boolean>;
  showAgentSection$: Observable<boolean>;

  constructor(
    private jwtService: JwtService,
    private titleService: TitleService,
    public translate: TranslateService,
    public userService: UserPlatformService,
    private agentService: AgentService,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
    this.mainMenuItems = this.mainMenuItems.map(item => ({
      ...item,
      icon: this.sanitizer.bypassSecurityTrustHtml(item.icon as string)
    }));
    this.agentMenuItems = this.agentMenuItems.map(item => ({
      ...item,
      icon: this.sanitizer.bypassSecurityTrustHtml(item.icon as string)
    }));

    this.isAgent$ = this.agentService.isAgent();
    this.isAdmin$ = this.agentService.isAdmin();

    // Show admin section only for admin/platform users
    this.showAdminSection$ = this.isAdmin$;

    // Show agent section for everyone (agents see only this; admins see this + admin)
    this.showAgentSection$ = combineLatest([this.isAgent$, this.isAdmin$]).pipe(
      map(([isAgent, isAdmin]) => isAgent || isAdmin)
    );

    // Filter menu items based on agent status
    this.mainMenuItems$ = this.isAgent$.pipe(
      map(isAgent =>
        isAgent
          ? this.mainMenuItems.filter(item => item.route !== '/dashboard' && item.route !== '/online-dashboard')
          : this.mainMenuItems
      )
    );
  }
  hastTitle = false;

  user = {
    name: '',
    organisation: '',
    role: ''
  };

  mainMenuItems: MenuItem[] = [
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 1v6m8-6v6M3 7h18"></path></svg>',
      label: 'SIDEBAR.DASHBOARD',
      route: '/dashboard'
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>',
      label: 'SIDEBAR.ONLINE_DASHBOARD',
      route: '/online-dashboard'
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>',
      label: 'SIDEBAR.ADMIN_ORDERS',
      route: '/admin-orders'
    },
  ];

    agentMenuItems: MenuItem[] = [
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>',
      label: 'SIDEBAR.DAY_ORDERS',
      route: '/agent-dashboard',
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>',
      label: 'SIDEBAR.UPCOMING_ORDERS',
      route: '/agent-upcoming-orders',
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>',
      label: 'SIDEBAR.SHOP_ORDERS',
      route: '/shop-orders',
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>',
      label: 'SIDEBAR.CALL_ORDERS',
      route: '/order',
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>',
      label: 'SIDEBAR.DELIVERY_CALCULATOR',
      route: '/agent-delivery-calculator',
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>',
      label: 'SIDEBAR.STOCK_LEVELS',
      route: '/stock-levels',
    }
  ];


  ngOnInit() {

    if (this.agentService.isAgentValue()) {
        logDebug('User is an agent');
    }

    // S'abonner aux changements du token
    this.tokenSubscription = this.jwtService.token$
    .pipe(
      distinctUntilChanged((prev, curr) => {
        // Compare les tokens pour éviter les exécutions inutiles
        return prev?.sub === curr?.sub;
      })
    )
    .subscribe(decodedToken => {
      try {
        if (decodedToken && decodedToken.name && decodedToken.sub) {
          // Utilise le nom complet du token s'il existe
          this.user.name = decodedToken.name;
          const match = decodedToken.sub.match(/@([^.]+)\./);
          const organisation = match ? match[1] : "";
          this.user.organisation = decodedToken.organization ?? organisation.toUpperCase();
        } else {
          // Si pas de token, réinitialiser le nom
          this.user.name = '';
          this.user.organisation = '';
        }
      } catch (error) {
        logError('Error processing token in header component:', error);
        this.user.name = '';
        this.user.organisation = '';
      }
    });

    // Correction: s'abonner au rôle pour mettre à jour user.role
    this.hastTitle = this.titleService.getHasTitle();

    // Forcer une mise à jour du token observable au cas où il serait déjà disponible
    setTimeout(() => {
      try {
        this.jwtService.updateTokenObservable();
      } catch (error) {
        logError('Error updating token observable:', error);
      }
    }, 100);


    // S'abonner à l'utilisateur connecté (filtrer les valeurs null)
    this.userService.getCurrentUser()
      .pipe(
        filter(user => user !== null) // Ignore les valeurs null
      )
      .subscribe(currentUser => {
        // Optionnel : mettre à jour les infos du user
        if (currentUser) {
          this.user.name = `${currentUser.firstname} ${currentUser.surname}`;
          // this.user.role = currentUser.appId;
        }
      })
  }

  getInitials(name: string): string {
    return name.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  ngOnDestroy(): void {
    // Nettoyer l'abonnement pour éviter les fuites mémoire
    if (this.tokenSubscription) {
      this.tokenSubscription.unsubscribe();
    }
  }

  onLinkClick(): void {
    this.linkClick.emit();
  }

  /**
   * Check if a route is active
   * Special handling for /dashboard route to include /day-products
   */
  isRouteActive(route: string): boolean {
    const currentUrl = this.router.url;

    // Exact match for the route
    if (currentUrl === route) {
      return true;
    }

    // Special case: /day-products should activate /dashboard
    if (route === '/dashboard' && currentUrl.startsWith('/day-products')) {
      return true;
    }

    // For other routes, check if current URL starts with the route
    // but only if it's followed by a query parameter or fragment
    if (currentUrl.startsWith(route + '?') || currentUrl.startsWith(route + '#')) {
      return true;
    }

    return false;
  }

}